import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowRight, Loader2 } from 'lucide-react';
import { useBlogPosts, useBlogCategories, useSettings } from '@/hooks/useProducts';
import { cn, formatDate, truncate } from '@/lib/utils';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import Pagination from '@/components/ui/Pagination';
import DocumentHead from '@/components/DocumentHead';
import { useSiteSettingMutation } from '@/hooks/useSiteSettingMutation';

export default function BlogList() {
  const { t } = useTranslation('blog');
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const activeCategory = searchParams.get('category') || '';

  const { data: postsData, isLoading: postsLoading } = useBlogPosts(
    page,
    activeCategory || undefined
  );
  const { data: categoriesData } = useBlogCategories();
  const { data: settingsData } = useSettings();
  const { isActive } = useQuickEdit();
  // One save path for every quick-edit field on this component: the hook
  // owns the cache invalidation, so it cannot be forgotten at a call site.
  const settingMutation = useSiteSettingMutation();
  const saveSetting = (settings: Record<string, unknown>) =>
    settingMutation.mutateAsync(settings);
  const blogTitle = settingsData?.settings?.blog_page_title || t('documentHead.title');
  const blogDescription = settingsData?.settings?.blog_page_description || t('documentHead.description');

  const posts = postsData?.posts ?? [];
  const total = postsData?.total ?? 0;
  const totalPages = Math.ceil(total / 10);
  const categories = categoriesData?.categories ?? [];

  function handleCategoryChange(slug: string) {
    const params = new URLSearchParams();
    if (slug) params.set('category', slug);
    setSearchParams(params);
  }

  function handlePageChange(newPage: number) {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <DocumentHead
        title={blogTitle}
        description={blogDescription}
        canonicalUrl="/blog"
      />
      <div className="text-center">
        <h1 className="text-3xl font-bold text-ink sm:text-4xl">
          {isActive ? (
            <QuickEditInline
              value={blogTitle}
              onSave={async (val) => {
                await saveSetting({ blog_page_title: val });
              }}
              label={t('quickEdit.blogTitleLabel')}
              as="span"
              className="text-3xl font-bold text-ink sm:text-4xl"
            />
          ) : blogTitle}
        </h1>
        <p className="mx-auto mt-2 max-w-xl text-ink-soft">
          {isActive ? (
            <QuickEditInline
              value={blogDescription}
              onSave={async (val) => {
                await saveSetting({ blog_page_description: val });
              }}
              label={t('quickEdit.blogDescriptionLabel')}
              as="span"
              className="text-ink-soft"
              multiline
            />
          ) : blogDescription}
        </p>
      </div>

      {/* Category filter */}
      {categories.length > 0 && (
        <nav aria-label={t('filters.ariaLabel')} className="mt-8">
          <ul className="flex flex-wrap justify-center gap-2">
            <li>
              <button
                onClick={() => handleCategoryChange('')}
                className={cn(
                  'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500',
                  !activeCategory
                    ? 'bg-sage-600 text-white'
                    : 'bg-surface-muted text-ink-soft hover:bg-surface-muted'
                )}
                aria-pressed={!activeCategory}
              >
                {t('filters.allPosts')}
              </button>
            </li>
            {categories.map((cat: any) => (
              <li key={cat.id}>
                <button
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={cn(
                    'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500',
                    activeCategory === cat.slug
                      ? 'bg-sage-600 text-white'
                      : 'bg-surface-muted text-ink-soft hover:bg-surface-muted'
                  )}
                  aria-pressed={activeCategory === cat.slug}
                >
                  {cat.name}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Loading state */}
      {postsLoading && (
        <div className="mt-12 flex justify-center" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-sage-600" aria-hidden="true" />
          <span className="sr-only">{t('list.loadingAriaLabel')}</span>
        </div>
      )}

      {/* Posts grid */}
      {!postsLoading && posts.length > 0 && (
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post: any, index: number) => {
            const isFeatured = index === 0 && page === 1 && !activeCategory;

            return (
              <article
                key={post.id}
                className={cn(
                  'group overflow-hidden rounded-xl border border-line bg-surface shadow-sm transition-shadow hover:shadow-md',
                  isFeatured && 'sm:col-span-2 lg:col-span-3'
                )}
              >
                <div className={cn(isFeatured ? 'lg:flex' : '')}>
                  {/* Featured image */}
                  <Link
                    to={`/blog/${post.slug}`}
                    className={cn(
                      'block overflow-hidden bg-surface-muted',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-inset',
                      isFeatured
                        ? 'aspect-video lg:w-1/2 lg:aspect-auto'
                        : 'aspect-video'
                    )}
                  >
                    {post.featured_image_url ? (
                      <img
                        src={post.featured_image_url}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full min-h-[200px] w-full items-center justify-center">
                        <Calendar className="h-10 w-10 text-ink-muted" aria-hidden="true" />
                      </div>
                    )}
                  </Link>

                  {/* Content */}
                  <div className={cn('p-5', isFeatured && 'lg:flex lg:flex-col lg:justify-center lg:p-8')}>
                    {post.category_name && (
                      <span className="text-xs font-semibold uppercase tracking-wider text-sage-700">
                        {post.category_name}
                      </span>
                    )}
                    <h2
                      className={cn(
                        'mt-1 font-bold text-ink',
                        isFeatured ? 'text-2xl' : 'text-lg'
                      )}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    {(post.excerpt || post.summary) && (
                      <p className={cn('mt-2 text-ink-soft', isFeatured ? 'text-base' : 'text-sm')}>
                        {truncate(post.excerpt || post.summary, isFeatured ? 250 : 120)}
                      </p>
                    )}
                    <div className="mt-4 flex items-center gap-4 text-xs text-ink-muted">
                      {post.author_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" aria-hidden="true" />
                          {post.author_name}
                        </span>
                      )}
                      {post.published_at && (
                        <time dateTime={post.published_at} className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                          {formatDate(post.published_at)}
                        </time>
                      )}
                    </div>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-sage-700 hover:text-sage-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                    >
                      {t('list.readMore')}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!postsLoading && posts.length === 0 && (
        <div className="mt-16 text-center">
          <Calendar className="mx-auto h-12 w-12 text-ink-muted" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-semibold text-ink">
            {t('list.emptyHeading')}
          </h2>
          <p className="mt-2 text-ink-soft">
            {activeCategory
              ? t('list.emptyDescriptionFiltered')
              : t('list.emptyDescriptionAll')}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-12 flex justify-center">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </main>
  );
}
